/**
 * Backfill missing width/height on photo size-variant entries.
 *
 * PhotoUtil.getNearestSize (@dreambooks/core) picks a variant by AREA
 * (width × height). Entries persisted without dimensions (recovery/re-sync
 * writebacks whose backend bodies omit them, or carry them under other keys)
 * all compare as area 0, the comparison loop never advances past the first
 * entry, and the renderer permanently loads the FIRST size in the array —
 * typically `_ts`/`_t` — producing blurry photos regardless of element size.
 *
 * The estimates use the server's generation rules (max long-edge per variant)
 * scaled to the photo's aspect ratio. They only FILL missing/zero dims —
 * real dimensions are never overwritten — and only need to be monotonic and
 * plausible for nearest-size ranking to work.
 */

/** Server max long-edge per size variant suffix. */
export const PHOTO_VARIANT_MAX_DIM: Record<string, number> = {
  ts: 100,
  t: 180,
  s: 320,
  m: 500,
  b: 800,
  l: 1500,
  e: 2000
};

/**
 * Variant suffix parsed from an entry's name/serverName/url
 * (same tolerant matcher the editor's cached renderer uses).
 */
export function getPhotoSizeVariant(size: {
  name?: string;
  serverName?: string;
  url?: string;
} | null | undefined): string | null {
  if (!size) return null;
  const raw = `${size.name || ''}|${(size as any).serverName || ''}|${size.url || ''}`.toLowerCase();
  const match = raw.match(/(?:^|[_/.-])(ts|t|s|m|b|l|e)(?=(?:\.[a-z0-9]+)?(?:\?|#|$|[|]))/);
  return match?.[1] ?? null;
}

/**
 * Fill width/height on entries that lack them (0/NaN/undefined), in place.
 * Returns the number of entries that were backfilled.
 *
 * @param sizes       size entries ({ name?, serverName?, url?, width?, height? })
 * @param photoWidth  original photo width, for aspect-correct estimates
 * @param photoHeight original photo height
 */
export function fillMissingPhotoSizeDimensions(
  sizes: Array<{ name?: string; url?: string; width?: number; height?: number }> | null | undefined,
  photoWidth?: number,
  photoHeight?: number
): number {
  return normalizePhotoSizeDimensions(sizes, photoWidth, photoHeight, false);
}

/**
 * Normalize width/height on size-variant entries, in place. Two repairs:
 *
 * 1. Missing dims (0/NaN/undefined) — estimated from the variant cap.
 * 2. When `fixInflated` is set, IMPLAUSIBLE dims: an entry claiming a long
 *    edge beyond its variant's server cap (e.g. every variant persisted with
 *    the ORIGINAL photo dimensions — an old writeback bug). Equal areas make
 *    PhotoUtil.getNearestSize keep the FIRST entry regardless of the target
 *    (`_t`), so the claimed dims must be re-estimated down to the cap.
 *
 * Returns the number of entries repaired.
 */
export function normalizePhotoSizeDimensions(
  sizes: Array<{ name?: string; url?: string; width?: number; height?: number }> | null | undefined,
  photoWidth?: number,
  photoHeight?: number,
  fixInflated: boolean = true
): number {
  if (!sizes || sizes.length === 0) return 0;

  const origW = Number(photoWidth) || 0;
  const origH = Number(photoHeight) || 0;
  let repaired = 0;

  for (const size of sizes) {
    const variant = getPhotoSizeVariant(size);
    const maxDim = variant ? PHOTO_VARIANT_MAX_DIM[variant] : undefined;
    if (!maxDim) continue;

    const w = Number(size.width) || 0;
    const h = Number(size.height) || 0;
    const missing = w <= 0 || h <= 0;
    // 10% tolerance — server caps are approximate, only flag clear lies.
    const inflated = fixInflated && !missing && Math.max(w, h) > maxDim * 1.1;
    if (!missing && !inflated) continue;

    if (origW > 0 && origH > 0) {
      // The server never upscales: cap the estimate at the original size.
      const scale = Math.min(1, maxDim / Math.max(origW, origH));
      size.width = Math.max(1, Math.round(origW * scale));
      size.height = Math.max(1, Math.round(origH * scale));
    } else if (!missing) {
      // Inflated with unknown original: scale the claimed dims down to the cap.
      const scale = maxDim / Math.max(w, h);
      size.width = Math.max(1, Math.round(w * scale));
      size.height = Math.max(1, Math.round(h * scale));
    } else {
      // Aspect unknown — a square estimate still gives the ranking a
      // monotonic, plausible area per variant.
      size.width = maxDim;
      size.height = maxDim;
    }
    repaired++;
  }

  return repaired;
}
